import { useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap"

import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import { ACCESS } from "constants/accessPermissions"
import { useLeaveTypes, useUpdateLeaveBalance } from "hooks/leaveHook"
import apiClient from "api/axios"

const LeaveBalancePage = () => {
  document.title = "Leave Balances"

  const [year, setYear] = useState(new Date().getFullYear())
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [modal, setModal] = useState({ open: false, employeeId: null, leaveTypeId: null, currentAllocated: "" })
  const [form, setForm] = useState({ allocated_days: "" })

  const { data: leaveTypesData } = useLeaveTypes({})
  const leaveTypes = leaveTypesData?.data ?? []
  const updateMutation = useUpdateLeaveBalance()

  const [balances, setBalances] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchBalances = async () => {
    if (!employeeFilter) return
    setLoading(true)
    try {
      const { data } = await apiClient.get("/leave-requests/my-balances", {
        params: { year, employee_id: employeeFilter },
      })
      setBalances(data?.data ?? [])
    } catch {
      toast.error("Failed to fetch balances")
    } finally {
      setLoading(false)
    }
  }

  const openAdjust = (employeeId, leaveTypeId, current) => {
    setModal({ open: true, employeeId, leaveTypeId, currentAllocated: current })
    setForm({ allocated_days: current })
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        employee_id: modal.employeeId,
        leave_type_id: modal.leaveTypeId,
        year,
        allocated_days: form.allocated_days,
      })
      toast.success("Balance updated")
      setModal({ open: false, employeeId: null, leaveTypeId: null, currentAllocated: "" })
      fetchBalances()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed")
    }
  }

  return (
    <PageWrapper title="Leave Balances">
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={2}>
              <select className="form-select" value={year} onChange={e => setYear(e.target.value)}>
                {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </Col>
            <Col md={3}>
              <InputField
                type="number"
                placeholder="Employee ID"
                value={employeeFilter}
                onChange={setEmployeeFilter}
              />
            </Col>
            <Col md={2}>
              <Button color="primary" onClick={fetchBalances} disabled={loading}>
                Load
              </Button>
            </Col>
          </Row>

          {balances.length > 0 && (
            <Table responsive>
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>Allocated</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {balances.map(b => (
                  <tr key={b.id}>
                    <td>{b.leave_type?.name}</td>
                    <td>{b.allocated_days}</td>
                    <td>{b.used_days}</td>
                    <td>
                      <Badge color={parseFloat(b.allocated_days) - parseFloat(b.used_days) > 0 ? "success" : "danger"}>
                        {parseFloat(b.allocated_days) - parseFloat(b.used_days)}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        color="warning"
                        onClick={() => openAdjust(b.employee_id, b.leave_type_id, b.allocated_days)}
                      >
                        Adjust
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modal.open} toggle={() => setModal({ open: false, employeeId: null, leaveTypeId: null, currentAllocated: "" })}>
        <ModalHeader>Adjust Leave Balance</ModalHeader>
        <ModalBody>
          <InputField
            label="Allocated Days"
            type="number"
            value={form.allocated_days}
            onChange={v => setForm({ allocated_days: v })}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave} disabled={updateMutation.isPending}>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModal({ open: false, employeeId: null, leaveTypeId: null, currentAllocated: "" })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default LeaveBalancePage